import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button as MuiButton,
  Typography,
  Box,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { debounce, get, uniq } from 'lodash';
import {
  searchPractitioners,
  createPractitioner,
} from 'modules/Administration/PatientRegistry/Practitioners/_api';
import {
  getNameTranslated,
  getAddressTranslated,
} from 'modules/Administration/PatientRegistry/_grid';
import PractitionerFormComponent from 'modules/Administration/PatientRegistry/Practitioners/PractitionerFormComponent';
import {
  IPractitioner,
  IPractitionerForm,
} from 'modules/Administration/PatientRegistry/Practitioners/_types';
import { updatePatient } from './_api';
import useAlerts from 'components/Alerts/useAlerts';
import { format } from 'date-fns';

interface PractitionerAddDialogProps {
  open: boolean;
  onClose: () => void;
  patient: any;
  onAdded: () => void;
}

const PractitionerAddDialog: React.FC<PractitionerAddDialogProps> = ({
  open,
  onClose,
  patient,
  onAdded,
}) => {
  const { t } = useTranslation(['Patients', 'Practitioners', 'Common']);
  const { addSuccessAlert, addErrorAlert } = useAlerts();

  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<IPractitioner[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [mode, setMode] = useState<'search' | 'create'>('search');

  const handleClose = () => {
    setSearchQuery('');
    setResults([]);
    setIsSearching(false);
    setMode('search');
    onClose();
  };

  const debouncedSearch = React.useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query || query.length < 3) {
          setResults([]);
          return;
        }
        setIsSearching(true);
        try {
          const criteria = {
            searchTerm: query,
          };
          const data = await searchPractitioners(criteria);
          if (data) {
            setResults(data);
          } else {
            setResults([]);
          }
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setIsSearching(false);
        }
      }, 500),
    [],
  );

  useEffect(() => {
    if (open && mode === 'search') {
      debouncedSearch(searchQuery);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch, open, mode]);

  const handleLinkPractitioner = async (practitionerId: number) => {
    const currentIds = get(patient, 'generalPractitionerIds', []) || [];
    if (currentIds.includes(practitionerId)) {
      addErrorAlert(t('alreadyLinked'));
      return;
    }

    try {
      const updatedIds = uniq([...currentIds, practitionerId]);
      await updatePatient({ ...patient, generalPractitionerIds: updatedIds });
      addSuccessAlert(t('linkingSuccess'));
      onAdded();
      handleClose();
    } catch (error) {
      addErrorAlert(t('linkingError'));
    }
  };

  const handleCreateAndLink = async (formData: IPractitionerForm) => {
    try {
      const birthDate = formData.birthDate;
      const names = (formData.name || []).map((name: any) => ({
        ...name,
        given: (name.given || []).filter((value: string) => !!value),
        prefix: (name.prefix || []).filter((value: string) => !!value),
        suffix: (name.suffix || []).filter((value: string) => !!value),
      }));
      const payload = {
        ...formData,
        name: names,
        birthDate: birthDate ? format(new Date(birthDate as any), 'yyyy-MM-dd') : null,
      } as IPractitionerForm;

      const newPractitioner = await createPractitioner(payload);
      if (newPractitioner && newPractitioner.id) {
        await handleLinkPractitioner(newPractitioner.id);
      }
    } catch (error) {
      addErrorAlert(t('Practitioners:createError'));
    }
  };

  const initialFormValues: IPractitionerForm = {
    name: [{ family: '', given: [''], prefix: [''], suffix: [''] }],
    active: true,
    identifier: [],
    telecom: [],
    address: [],
    organizationIds: [],
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {mode === 'create' && (
            <IconButton onClick={() => setMode('search')} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6">
            {mode === 'search' ? t('addPractitioner') : t('Practitioners:registerNew')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: mode === 'create' ? 0 : 2 }}>
        {mode === 'search' ? (
          <Box>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('Practitioners:searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              helperText={searchQuery.length < 3 ? t('typeAtLeast3Characters') : ' '}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: isSearching ? <CircularProgress size={20} /> : null,
              }}
              sx={{ mb: 2 }}
            />
            <List>
              {results.map((p) => (
                <React.Fragment key={p.id}>
                  <ListItem>
                    <ListItemText
                      primary={getNameTranslated(p.name)}
                      secondary={getAddressTranslated(p.address || [])}
                    />
                    <ListItemSecondaryAction>
                      <MuiButton
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => handleLinkPractitioner(p.id!)}
                        disabled={(get(patient, 'generalPractitionerIds', []) || []).includes(p.id)}
                      >
                        {t('connect')}
                      </MuiButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
              {!isSearching && searchQuery && results.length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography color="textSecondary" gutterBottom>
                    {t('noResultsFound')}
                  </Typography>
                  <MuiButton
                    variant="contained"
                    color="primary"
                    sx={{ mt: 1 }}
                    onClick={() => setMode('create')}
                  >
                    {t('Practitioners:registerNew')}
                  </MuiButton>
                </Box>
              )}
            </List>
          </Box>
        ) : (
          <PractitionerFormComponent
            isCreating={true}
            defaultValues={initialFormValues}
            onSubmit={handleCreateAndLink}
            linkBack=""
            compactMode={true}
            title=""
          />
        )}
      </DialogContent>
      {mode === 'search' && (
        <DialogActions>
          <MuiButton onClick={() => setMode('create')} color="primary" variant="contained">
            {t('Practitioners:registerNew')}
          </MuiButton>
          <MuiButton onClick={handleClose}>{t('Buttons:close')}</MuiButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default PractitionerAddDialog;
